import { computed, ref } from 'vue';

/**
 * A composable for styling picture
 * @param {object} options
 * @param {import('vue').Ref<Number | null>} options.opacity - opacity of picture
 * @param {import('vue').Ref<String | null>} options.filter - filter color to blend with picture
 * @param {import('vue').Ref<Boolean>} options.showSkeleton - show skeleton placeholder
 * @param {import('vue').Ref<object>} options.skeletonOptions - skeleton placeholder options
 * @param {import('vue').Ref<Number | null>} options.imageIntrinsicWidth - define width used by image placeholder or skeleton
 * @param {import('vue').Ref<Number | 0.8>} options.aspectRatio - specified image aspect ratio
 */
export default function useStyles({
  opacity = ref(null),
  filter = ref(null),
  showSkeleton = ref(false),
  skeletonOptions = ref({}),
  imageIntrinsicWidth = ref(null),
  aspectRatio = ref(0.8),
}) {
  /**
  * Styling for picture container
  * @returns {import('vue').ComputedRef<Object>} computed styles
  */
  const pictureContainerStyles = computed(() => {
    let styles = {};
    if (showSkeleton.value && skeletonOptions.value.paddingBottom) {
      styles = {
        ...styles,
        'padding-bottom': skeletonOptions.value.paddingBottom,
      };
    }
    if (opacity.value) {
      styles = {
        ...styles,
        backgroundColor: 'black',
      };
    }
    if (filter.value) {
      styles = {
        ...styles,
        backgroundColor: filter.value,
      };
    }
    return styles;
  });

  /**
  * Styling for image element
  * @returns {import('vue').ComputedRef<Object>} computed styles
  */
  const imageStyles = computed(() => {
    if (opacity.value) {
      return { opacity: opacity.value };
    }
    if (filter.value) {
      return { mixBlendMode: 'multiply' };
    }
    return null;
  });

  /**
  * Styling for image fallback placeholder element
  * @returns {import('vue').ComputedRef<Object>} computed styles
  */
  const fallbackStyles = computed(() => {
    if (imageIntrinsicWidth.value === null) {
      return {
        width: '100%',
        'padding-top': `${(1 / aspectRatio.value) * 100}%`,
      };
    }
    return {
      width: `${imageIntrinsicWidth.value}px`,
      'padding-top': `${imageIntrinsicWidth.value / aspectRatio.value}px`,
    };
  });

  return {
    pictureContainerStyles,
    imageStyles,
    fallbackStyles,
  };
}
